import React from "react";
import { Link } from "react-router-dom";
import { useInView } from "react-intersection-observer";
import { typography, spacing, components } from "../styles/designSystem";
import { IN_VIEW_OPTIONS, reveal } from "../styles/animations";
import PageHeader from "./PageHeader";

/**
 * Subtle background used on legal pages (Privacy Policy, T&C).
 * Positioned slightly differently via the `variant` prop.
 */
const LegalBackground = ({ variant = "default" }) => (
  <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-primary/[0.03]" />

    {variant === "privacy" ? (
      <>
        <div className="absolute -left-20 top-40 h-64 w-64 rounded-full bg-accent/[0.06] blur-3xl" />
        <div className="absolute -right-16 bottom-[28%] h-56 w-56 rounded-full bg-primary/[0.05] blur-3xl" />
        <div className="absolute left-[14%] top-[22%] h-3 w-3 rounded-full bg-accent/35" />
        <div className="absolute right-[10%] bottom-[20%] h-5 w-5 rounded-full border-[3px] border-primary/20" />
      </>
    ) : (
      <>
        <div className="absolute -right-24 top-32 h-72 w-72 rounded-full bg-primary/[0.06] blur-3xl" />
        <div className="absolute -left-16 bottom-[30%] h-48 w-48 rounded-full bg-accent/[0.05] blur-3xl" />
        <div className="absolute right-[12%] top-[20%] h-3 w-3 rounded-full bg-primary/30" />
        <div className="absolute left-[8%] bottom-[25%] h-5 w-5 rounded-full border-[3px] border-accent/25" />
      </>
    )}
  </div>
);

/**
 * Content card used in legal pages — rounded card with subtle decoration.
 */
const LegalContentCard = ({
  sectionRef,
  visible,
  intro,
  sections,
  contactText,
  variant,
}) => (
  <div ref={sectionRef} className={`max-w-4xl mx-auto ${reveal(visible)}`}>
    <div className="relative overflow-hidden rounded-3xl border border-secondary/10 bg-white p-8 shadow-xl md:p-12">
      {/* Subtle decorative corner */}
      <div
        className={`pointer-events-none absolute h-32 w-32 rounded-full blur-2xl ${
          variant === "privacy"
            ? "-left-10 -bottom-10 bg-accent/[0.05]"
            : "-right-10 -top-10 bg-primary/[0.05]"
        }`}
      />

      <div className="relative space-y-10">
        <p
          className={`${typography.body.base} text-gray-700 pb-6 border-b border-gray-200/60`}
        >
          {intro}
        </p>

        {sections.map((section, index) => (
          <div
            key={index}
            className="space-y-3 pb-8 border-b border-gray-200/60 last:border-b-0 last:pb-0"
          >
            <h2 className={`${typography.heading.h4} text-secondary`}>
              {section.title}
            </h2>
            <p className={`${typography.body.base} text-gray-700`}>
              {section.text}
            </p>
          </div>
        ))}

        {/* Contact box */}
        <div className="mt-10 pt-10 border-t border-gray-200/60 rounded-2xl bg-gradient-to-br from-primary/[0.04] to-accent/[0.04] p-6 text-center space-y-4">
          <p className={`${typography.body.base} text-gray-700`}>
            {contactText}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-primary font-semibold hover:text-secondary transition-colors duration-300"
          >
            Contact us
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  </div>
);

/**
 * Full legal page layout — background, header, content card, back button.
 *
 * @param {object} props
 * @param {string} props.label – header label (e.g. "Legal")
 * @param {React.ReactNode} props.title – page title
 * @param {string} props.subtitle – header subtitle
 * @param {string} props.intro – intro paragraph inside the card
 * @param {Array} props.sections – array of { title, text } objects
 * @param {string} props.contactText – text above the contact link
 * @param {"default"|"privacy"} [props.variant] – visual variant
 */
const LegalLayout = ({
  label,
  title,
  subtitle,
  intro,
  sections,
  contactText,
  variant = "default",
}) => {
  const { ref: headerRef, inView: headerInView } = useInView(IN_VIEW_OPTIONS);
  const { ref: contentRef, inView: contentInView } = useInView(IN_VIEW_OPTIONS);

  return (
    <main className="relative min-h-screen overflow-hidden bg-white pt-24 md:pt-32">
      <LegalBackground variant={variant} />

      <section
        className={`${spacing.container} ${spacing.section} relative z-10`}
      >
        <PageHeader
          sectionRef={headerRef}
          visible={headerInView}
          label={label}
          title={title}
          subtitle={subtitle}
        />

        <LegalContentCard
          sectionRef={contentRef}
          visible={contentInView}
          intro={intro}
          sections={sections}
          contactText={contactText}
          variant={variant}
        />

        <div className="mt-16 text-center">
          <Link to="/" className={`${components.button.secondary} px-8 py-4`}>
            Back to home
          </Link>
        </div>
      </section>
    </main>
  );
};

export default LegalLayout;
